import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Shop } from '@components/ui/shop/shop';
import { faLink, faTrash } from '@fortawesome/pro-light-svg-icons';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';

interface LinkedProps {
  onCreateLink: () => void;
  readOnly?: boolean;
  enlaces?: Array<{
    tienda: any;
    producto: any;
    variante: any;
    created_at?: string; // opcional: fecha proveniente de la API
  }>;
  onDeleteLink?: (index: number) => void;
  shops: any[];
  /** Modal de confirmación renderizado en Main (fuera de las tabs) */
  confirmModalRef: React.RefObject<ConfirmModalRef | null>;
}

export const Linked = ({
  onCreateLink,
  enlaces = [],
  onDeleteLink,
  shops,
  readOnly = false,
  confirmModalRef,
}: LinkedProps) => {
  const { t } = useTranslation('createEditProductsModule');
  const { toast } = useToast();

  return (
    <>
      <div className="space-y-4 pb-6 gap-4 grid grid-cols-6 pt-4 mb-0">
        <div className="col-span-5 md:col-span-2">
          <h2 className="text-lg font-semibold leading-tight mb-1">{t('sections.enlazar_title')}</h2>
          <p className="text-sm">{t('sections.enlazar_desc')}</p>
        </div>
        {!readOnly && (
          <div className="col-span-1 md:col-span-4 !mt-0 flex items-start justify-end">
            <Button
              variant="outline"
              size="default"
              className="h-9 px-4 flex items-center gap-2"
              onClick={onCreateLink}
            >
              <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
              <span className="hidden sm:inline-block">{t('sections.enlazar_boton')}</span>
            </Button>
          </div>
        )}
      </div>
      <div className="w-full divide-y divide-lines">
        {enlaces.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12 min-h-[55vh]">
            <img
              src="/imagen/empty-tables/empty-product-link-light.svg"
              alt="Sin enlaces"
              className="w-24 h-24 mb-4 opacity-50"
            />
            <h3 className="text-lg font-medium text-title mb-2">{t('empty_link.title')}</h3>
            <p className="text-subtext">{t('empty_link.description')}</p>
          </div>
        ) : (
          enlaces.map((enlace, idx) => {
            // Buscar nombre de tienda por shop_id
            let shopName = enlace.tienda?.label || enlace.tienda?.name;
            const shopIdValue = 'shop_id' in enlace ? enlace.shop_id : 'shopId' in enlace ? enlace.shopId : undefined;
            if (!shopName && shopIdValue) {
              const shop = shops.find((s) => String(s.id) === String(shopIdValue));
              shopName = shop?.name;
            }
            const shop = shops.find((s) => String(s.id) === String(shopIdValue));
            // Mostrar título: si existe 'title' (API), mostrarlo; si no, producto + variante
            let title = '-';
            if ('title' in enlace && enlace.title) {
              title = String(enlace.title);
            } else {
              const productoName = enlace.producto?.name || enlace.producto?.label || '';
              const varianteName = enlace.variante?.label || enlace.variante?.name || '';
              if (productoName && varianteName) {
                title = `${productoName} ${varianteName}`;
              } else if (productoName) {
                title = productoName;
              } else if (varianteName) {
                title = varianteName;
              }
            }
            const formattedDate = enlace.created_at
              ? new Date(enlace.created_at).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : '-';

            const handleDelete = () => {
              // Abre confirmación; si confirma, elimina
              confirmModalRef.current?.open(async () => {
                try {
                  const http = createHttpClient(apiClientWH);
                  const adapter = new ProductsApiAdapter(http);
                  const id = (enlace as any).id || (enlace as any).linked_product_id;
                  await adapter.deleteExternalLink(id);
                  toast({
                    title: t('form.unlink_success_title'),
                    description: t('form.unlink_success_desc'),
                    variant: 'success',
                  });
                  onDeleteLink?.(idx);
                } catch (error: any) {
                  const data = error?.response?.data;
                  const apiMessage =
                    data?.message ||
                    data?.error?.message ||
                    (Array.isArray(data?.errors) && data.errors[0]?.message) ||
                    data?.error_description ||
                    error?.message;
                  toast({
                    title: t('form.unlink_error_title'),
                    description: apiMessage || t('form.error_desc'),
                    variant: 'destructive',
                  });
                }
              });
            };

            return (
              <div key={idx} className="flex items-center py-4 w-full">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{title}</div>
                  <div className="flex items-center gap-2 mt-1 text-sm">
                    <Shop
                      name={shopName}
                      src={
                        enlace.tienda?.shopObj?.image ||
                        enlace.tienda?.image ||
                        shop?.image ||
                        'https://placehold.co/32x32/png'
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="text-xs">
                    {t('sections.linked_on')}: {formattedDate}
                  </div>
                  {!readOnly && (
                    <button className="ml-4 py-2 px-1 text-icons" onClick={handleDelete}>
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};
